import React, { useEffect, useState, useCallback } from "react";
import { useQueryParam, withDefault, NumberParam } from "use-query-params";
import { Col, Row, Container } from "reactstrap";
import Header from "../../layout/Header";
import BookingCard from "../BookingCard";
import SubscriptionForm from "../SubscriptionForm";
import SubscriptionFinal from '../SubscriptionFinal';
import { usePublicEndpoints } from "../../../lib/api/usePublicEndpoints";
import _ from 'lodash';
import {useLogin} from "../../../lib/utils/hooks/useLogin";
import { t } from 'i18next';
import { useInjection } from 'brandi-react';
import { GlobalModelStoreToken } from '../../../lib/models/global';
import {calculatePromoAndCertDiscount} from "../../../lib/utils/helpers";
import {sendAnalyticsEvents} from "../../../lib/utils/analytics";
import useSendPurchaseAnalytics from "../../../lib/utils/hooks/useSendPurchaseAnalytics";
import {SubscriptionContainerModelToken} from "./model";
import {observer} from "mobx-react-lite";
import {useHandleClientParameters} from '../../../lib/utils/hooks/useHandleClientParameters';
import {MindboxModelStoreToken} from "../../../entities/mindbox/model";

const SubscriptionContainer = () => {
    const globalM = useInjection(GlobalModelStoreToken);
    const subscriptionContainerM = useInjection(SubscriptionContainerModelToken);
    const mindboxM = useInjection(MindboxModelStoreToken);

    const {isUserLoggedIn} = useLogin();

    const [subscription, setSubscription] = useState({});
    const [price, setPrice] = useQueryParam("price", NumberParam);
    const [step, setStep] = useQueryParam("step", withDefault(NumberParam, 1));
    const [email, setEmail] = useState("");
    const [order, setOrder] = useState({});
    const [orderId, setOrderId] = useQueryParam("orderId", NumberParam);
    const { getOrder, getUserCleanFields, getCurrencies } = usePublicEndpoints();
    const [currencies, setCurrencies] = useState([]);
    const [certificateApplied, setCertificateApplied] = useState(null);
    const [promoApplied, setPromoApplied] = useState(null);
    const [promoAndCertDiscount, setPromoAndCertDiscount] = useState({});
    const [entity, setEntity] = useState({});

    const debouncedHandleInput = useCallback(_.debounce((subscription) => {
        setPrice((+subscription.price_value));
        mindboxM.viewProduct('abonement', String(subscription.price_value));
    }, 200), []);

    const setValues = (subscription) => {
        setSubscription(() => subscription);
        debouncedHandleInput(subscription);
    };

    const globalState = globalM.get();
    const payingUser = subscriptionContainerM.payingUser;

    const paidDiscount = +order?.discountedValue

    useSendPurchaseAnalytics(order?.totalValue && {
        id: orderId,
        totalValue: paidDiscount,
        category: 'Abonement',
        paidDiscount: paidDiscount,
        name: order?.abonement_name,
    }, 'Abonement');

    useHandleClientParameters(payingUser?.id)

    useEffect(() => {
        setEntity(_.isEmpty(subscription) ? order.subscription : subscription);
    }, [order, subscription])

    useEffect(() => {
        if (!isUserLoggedIn) {
            setStep(1)
            setOrderId(undefined)
            setOrder({})
        }
        if (step === 1) sendAnalyticsEvents('date_main', 1);
    }, [step])

    useEffect(() => {
        getCurrencies()
            .then(res => setCurrencies(res))
        setEmail(() => globalState.userEmail);

        if (orderId) {
            getOrder(orderId)
                .then(res => {
                    setOrder(res)
                })
        }
    }, []);

    useEffect(() => {
        subscriptionContainerM.initial({userEmail: globalState.userEmail});
    }, [globalState.userEmail]);

    useEffect(() => {
        if (!orderId && entity?.id) {
            setOrder({
                totalValue: entity.price_value,
                discountedValue: entity.price_value,
                priceCurrency: entity.price_currency,
            });
        }
    }, [entity?.id]);

    useEffect(() => {
        if (entity?.id) {
            setPromoAndCertDiscount(calculatePromoAndCertDiscount(certificateApplied, promoApplied, order, entity));
        }
    }, [promoApplied?.id, certificateApplied?.id, order, entity?.id]);

    return (
        <Container className={"pl-0 pr-0"}>
            <Row className={"min-vh-100 no-gutters"}>
                <Col xs={12} lg={6} className={"form-col"}>
                    <Header label={t('subscriptions.headerTitle')} />
                    {step === 1 &&
                        <SubscriptionForm
                            setSubscription={(subscription) => setValues(subscription)}
                            email={email}
                            subscription={subscription}
                            price={price}
                            userSubscriptionId={payingUser?.subscription_type_id}
                            depositSum={payingUser?.deposit_sum}
                            currencies={currencies}
                            setCertificateApplied={setCertificateApplied}
                            certificateApplied={certificateApplied}
                            setPromoApplied={setPromoApplied}
                            promoAndCertDiscount={promoAndCertDiscount}
                            order={order}
                            entity={entity}
                            promoApplied={promoApplied}
                        />
                    }
                    {step === 2 &&
                        <SubscriptionFinal />
                    }
                </Col>
                <Col xs={6} className={"offset-sm-6 card-col min-vh-100 fixed-top d-none d-lg-block"}>
                    <BookingCard
                        entity={entity}
                        payingUser={payingUser}
                        hasPaid={order.hasPaid}
                        currencies={currencies}
                        price={promoAndCertDiscount.price}
                        oldPrice={entity?.price_value}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default observer(SubscriptionContainer);
